export type Language = 'en' | 'ar';

type TimelineItem = {
  date: string;
  title: string;
  subtitle: string;
  description: string;
};

type ProjectItem = {
  title: string;
  description: string;
  tech: string[];
  link?: string;
};

type SkillCard = {
  title: string;
  description: string;
  techLabel: string;
  tags: string[];
};

export type Translations = {
  nav: {
    hero: string;
    about: string;
    expertise: string;
    projects: string;
    contact: string;
    menu: string;
  };
  hero: {
    greeting: string;
    name: string;
    title: string;
    tagline: string;
    badges: string[];
    actions: {
      primary: string;
      secondary: string;
    };
  };
  about: {
    title: string;
    company: string;
    paragraphs: string[];
    highlights: { label: string; value: string }[];
    servicesTitle: string;
    services: string[];
  };
  skills: {
    title: string;
    cards: SkillCard[];
  };
  timeline: {
    title: string;
    items: TimelineItem[];
  };
  projects: {
    title: string;
    subtitle: string;
    items: ProjectItem[];
  };
  contact: {
    eyebrow: string;
    title: string;
    subtitle: string;
    cards: {
      phoneLabel: string;
      phoneValue: string;
      emailLabel: string;
      emailValue: string;
      locationLabel: string;
      locationValue: string;
    };
    form: {
      nameLabel: string;
      namePlaceholder: string;
      emailLabel: string;
      emailPlaceholder: string;
      projectTypeLabel: string;
      projectTypePlaceholder: string;
      projectTypeOptions: {
        web: string;
        mobile: string;
        desktop: string;
        dashboard: string;
        automation: string;
        other: string;
      };
      messageLabel: string;
      messagePlaceholder: string;
      submit: string;
      errors: {
        name: string;
        email: string;
        projectType: string;
        message: string;
      };
    };
  };
  footer: {
    text: string;
    author: string;
    builtWith: string;
  };
};

export const translations: Record<Language, Translations> = {
  en: {
    nav: {
      hero: 'Home',
      about: 'About Us',
      expertise: 'Skills',
      projects: 'Projects',
      contact: 'Contact',
      menu: 'Menu',
    },
    hero: {
      greeting: 'Welcome to',
      name: 'Bikmin Software Solutions',
      title: 'Software & Digital Solutions Company',
      tagline:
        'We design and build modern software including web platforms, mobile applications, dashboards, and automation tools that help businesses work smarter and grow faster.',
      badges: [
        'Custom Software Development',
        'Mobile Applications',
        'Web Platforms',
        'API Integration',
        'Data Automation & Visualization'
      ],
      actions: {
        primary: 'View Projects',
        secondary: 'Contact Us',
      },
    },
    about: {
      title: 'About Us',
      company: 'Bikmin Software Solutions',
      paragraphs: [
        'Bikmin Software Solutions is a technology company focused on building modern digital products that help businesses operate more efficiently.',
        'We develop custom systems, mobile applications, and web platforms designed to simplify business processes and improve productivity.',
        'Our team combines strong engineering with practical design to deliver reliable and scalable solutions tailored to each client.',
        'Our goal is to help organizations turn ideas into powerful digital products that create real business value.',
      ],
      highlights: [
        { label: 'Focus', value: 'Custom systems, mobile, web platforms' },
        { label: 'Approach', value: 'Scalable, reliable, high-performance solutions' },
        { label: 'Impact', value: 'Efficiency, automation, and better decisions' },
      ],
      servicesTitle: 'Our services',
      services: [
        'Custom Software Development',
        'Mobile Application Development',
        'Web Applications and Dashboards',
        'API Integration',
        'Data Processing and Visualization',
        'Business Automation Systems',
      ],
    },
    skills: {
      title: 'Skills',
      cards: [
        {
          title: 'Full Stack Web Development',
          description:
            'We build modern web, mobile, and desktop applications with reliable architectures and user-focused interfaces designed for performance and scalability.',
          techLabel: 'Tech stack',
          tags: ['React', 'TypeScript', 'JavaScript', 'HTML5', 'CSS3', 'SASS', 'Flask', 'Python', 'SQL', 'PostgreSQL', 'Postman', 'Mobile Apps', 'Desktop Apps'],
        },
        {
          title: 'DevOps & Automation',
          description:
            'We implement automation pipelines, testing systems, and cloud deployment workflows that keep products stable, secure, and easy to maintain.',
          techLabel: 'Tech stack',
          tags: ['Git', 'GitHub Actions', 'Docker', 'AWS', 'Azure', 'Linux', 'Snowflake', 'Pandas', 'Selenium'],
        },
        {
          title: 'GenAI & LLM',
          description:
            'We design AI-powered features and intelligent assistants that integrate smoothly into modern digital products and workflows.',
          techLabel: 'Tech stack',
          tags: ['OpenAI', 'Groq', 'LangChain', 'Qdrant', 'Hugging Face', 'LlamaIndex', 'Streamlit'],
        },
        {
          title: 'EdTech & AI-Powered Assessments',
          description:
            'We build advanced educational platforms that help institutions manage courses, students, and online exams. The system can generate question banks from course materials, create unique exams for each student, and run secure exam sessions with monitoring and identity verification.',
          techLabel: 'Tech stack',
          tags: ['Next.js', 'Node.js', 'PostgreSQL', 'OpenAI API', 'LangChain', 'Qdrant', 'RAG', 'Computer Vision', 'WebRTC', 'WebSockets', 'NLP', 'Python', 'FastAPI'],
        },
      ],
    },

    timeline: {
      title: 'Career History',
      items: [
        {
          date: '2022 - present',
          title: 'Technology Consultant',
          subtitle: 'Dallas, TX',
          description: 'Full-stack development, AI solutions, and technical project leadership.',
        },
        {
          date: '2020 - 2022',
          title: 'Full Stack Engineer',
          subtitle: 'Laie, HI',
          description: 'Frontend and backend engineering with focus on performance and user experience.',
        },
        {
          date: '2021',
          title: 'Staff Engineer Intern',
          subtitle: 'Laie, HI',
          description: 'Developed APIs and implemented full-stack product features.',
        },
        {
          date: '2020',
          title: 'Data Analyst Intern',
          subtitle: 'Tokyo, Japan',
          description: 'Worked on data automation, governance, and statistical analysis.',
        },
      ],
    },

    projects: {
      title: 'Projects',
      subtitle: 'Selected work from recent engagements.',
      items: [
        {
          title: 'A2Z Desktop Dashboard',
          description:
            'An operations dashboard that helps e-commerce teams manage platform activity, monitor performance, and organize daily workflows.',
          tech: ['Flutter', 'Cubit', 'REST API'],
        },
        {
          title: 'NCI Post Graduate System',
          description:
            'A management system designed to organize postgraduate programs, student records, and academic administration.',
          tech: ['Flutter', 'Provider', 'API'],
        },
        {
          title: 'Quizaty Platform',
          description:
            'A modern educational platform that allows institutions to manage courses, students, and online exams. The system generates question banks from textbooks and creates unique quizzes for every student within a secure exam environment.',
          tech: ['Next.js', 'Node.js', 'PostgreSQL', 'OpenAI API', 'LangChain', 'RAG', 'WebRTC', 'Computer Vision', 'FastAPI', 'WebSockets'],
          link: 'https://www.quizatty.com/Login',
        },
        {
          title: 'Shaq El Taaban System',
          description:
            'A digital platform and mobile solution designed to support the Shaq El Taaban marble and granite industrial area with better service coordination.',
          tech: ['Mobile', 'Operations', 'API'],
          link: 'https://www.shkelteaban.com/',
        },
        {
          title: 'Data Preprocessing & Visualization Automation – EgyBank',
          description:
            'A system built to automate banking data processing and generate clear visual insights for internal analysis.',
          tech: ['Automation', 'Data Viz', 'Banking'],
        },
        {
          title: 'Weather App',
          description:
            'A mobile application that provides real-time weather information through a simple and user-friendly interface.',
          tech: ['Mobile', 'Weather API', 'UI'],
        },
      ],
    },

    contact: {
      eyebrow: 'Contact',
      title: 'Start Your Project',
      subtitle:
        'Tell us about your project or idea and our team will get back to you with the next steps.',
      cards: {
        phoneLabel: 'Phone',
        phoneValue: '+20 120 424 6538',
        emailLabel: 'Email',
        emailValue: 'nzmfix4@gmail.com',
        locationLabel: 'Location',
        locationValue: 'Cairo, Egypt',
      },
      form: {
        nameLabel: 'Name',
        namePlaceholder: 'Your full name',
        emailLabel: 'Email',
        emailPlaceholder: 'you@company.com',
        projectTypeLabel: 'Project Type',
        projectTypePlaceholder: 'Select an option',
        projectTypeOptions: {
          web: 'Web platform',
          mobile: 'Mobile app',
          desktop: 'Desktop app',
          dashboard: 'Dashboard / Analytics',
          automation: 'Automation / API',
          other: 'Other',
        },
        messageLabel: 'Message',
        messagePlaceholder: 'Tell us about your project, goals, and timeline.',
        submit: 'Start a Project 🚀',
        errors: {
          name: 'Please enter your name',
          email: 'Please enter your email',
          projectType: 'Please select a project type',
          message: 'Please enter the message',
        },
      },
    },

    footer: {
      text: 'A portfolio designed & built by',
      author: 'Yuji Sato',
      builtWith: 'with care and creativity',
    },
  },

  ar: {
    nav: {
      hero: 'الرئيسية',
      about: 'من نحن',
      expertise: 'المهارات',
      projects: 'المشاريع',
      contact: 'التواصل',
      menu: 'القائمة',
    },

    hero: {
      greeting: 'مرحباً بكم في',
      name: 'Bikmin Software Solutions',
      title: 'شركة برمجيات وحلول رقمية',
      tagline:
        'نصمم ونطور برمجيات حديثة تشمل منصات الويب وتطبيقات الموبايل ولوحات التحكم وأنظمة الأتمتة لمساعدة الشركات على العمل بكفاءة أكبر والنمو بسرعة.',
      badges: [
        'تطوير البرمجيات المخصصة',
        'تطبيقات الموبايل',
        'منصات الويب',
        'تكامل واجهات API',
        'أتمتة البيانات وتصوّرها'
      ],
      actions: {
        primary: 'استعرض المشاريع',
        secondary: 'تواصل معنا',
      },
    },

    about: {
      title: 'من نحن',
      company: 'Bikmin Software Solutions',
      paragraphs: [
        'شركة Bikmin Software Solutions متخصصة في تطوير الحلول الرقمية الحديثة التي تساعد الشركات على تحسين عملياتها وزيادة كفاءتها.',
        'نطور أنظمة برمجية مخصصة وتطبيقات موبايل ومنصات ويب تساعد على تبسيط العمليات التجارية وتحسين الإنتاجية.',
        'يجمع فريقنا بين الخبرة التقنية والتصميم العملي لتقديم حلول موثوقة وقابلة للتوسع.',
        'هدفنا هو مساعدة الشركات على تحويل أفكارها إلى منتجات رقمية قوية تحقق قيمة حقيقية للأعمال.',
      ],
      highlights: [
        { label: 'مجالات التركيز', value: 'أنظمة مخصصة، موبايل، منصات ويب' },
        { label: 'منهجية العمل', value: 'حلول موثوقة وقابلة للتوسع وعالية الأداء' },
        { label: 'الأثر', value: 'كفاءة أعلى وأتمتة وقرارات أفضل' },
      ],
      servicesTitle: 'خدماتنا',
      services: [
        'تطوير البرمجيات المخصصة',
        'تطوير تطبيقات الموبايل',
        'تطوير مواقع الويب ولوحات التحكم',
        'التكامل مع APIs',
        'معالجة البيانات وتحليلها بصريًا',
        'أنظمة أتمتة الأعمال',
      ],
    },

    skills: {
      title: 'المهارات',
      cards: [
        {
          title: 'تطوير ويب متكامل',
          description:
            'نطور تطبيقات ويب وموبايل وسطح مكتب حديثة بهياكل قوية وتجارب استخدام سهلة وقابلة للتوسع.',
          techLabel: 'التقنيات',
          tags: ['React', 'TypeScript', 'JavaScript', 'HTML5', 'CSS3', 'SASS', 'Flask', 'Python', 'SQL', 'PostgreSQL', 'Postman', 'تطبيقات موبايل', 'تطبيقات سطح المكتب'],
        },
        {
          title: 'عمليات DevOps والأتمتة',
          description:
            'نبني أنظمة أتمتة واختبارات ونشر سحابي تضمن استقرار المنتجات وسهولة تطويرها وصيانتها.',
          techLabel: 'التقنيات',
          tags: ['Git', 'GitHub Actions', 'Docker', 'AWS', 'Azure', 'Linux', 'Snowflake', 'Pandas', 'Selenium'],
        },
        {
          title: 'الذكاء الاصطناعي والنماذج اللغوية',
          description:
            'نطور ميزات تعتمد على الذكاء الاصطناعي والمساعدات الذكية لدمج القدرات الذكية داخل التطبيقات الحديثة.',
          techLabel: 'التقنيات',
          tags: ['OpenAI', 'Groq', 'LangChain', 'Qdrant', 'Hugging Face', 'LlamaIndex', 'Streamlit'],
        },
        {
          title: 'منصات التعليم والتقييم الذكي',
          description:
            'نطور منصات تعليمية متكاملة تساعد المؤسسات على إدارة الطلاب والدورات والاختبارات الإلكترونية مع إنشاء بنوك أسئلة تلقائياً وتشغيل امتحانات آمنة مع أنظمة مراقبة وتحقق من الهوية.',
          techLabel: 'التقنيات',
          tags: ['Next.js', 'Node.js', 'PostgreSQL', 'OpenAI API', 'LangChain', 'Qdrant', 'RAG', 'Computer Vision', 'WebRTC', 'WebSockets', 'NLP', 'Python', 'FastAPI'],
        },
      ],
    },

    timeline: {
      title: 'المسيرة المهنية',
      items: [
        {
          date: '2022 - الآن',
          title: 'استشاري تقني',
          subtitle: 'دالاس، تكساس',
          description: 'تطوير أنظمة متكاملة وحلول ذكاء اصطناعي وقيادة المشاريع التقنية.',
        },
        {
          date: '2020 - 2022',
          title: 'مهندس برمجيات متكامل',
          subtitle: 'لاييه، هاواي',
          description: 'تطوير الواجهات والخلفيات مع التركيز على الأداء وتجربة المستخدم.',
        },
        {
          date: '2021',
          title: 'متدرب مهندس برمجيات',
          subtitle: 'لاييه، هاواي',
          description: 'تطوير واجهات برمجية وميزات متكاملة للمنتجات.',
        },
        {
          date: '2020',
          title: 'متدرب محلل بيانات',
          subtitle: 'طوكيو، اليابان',
          description: 'أتمتة البيانات وتحليلها وإعداد تقارير إحصائية.',
        },
      ],
    },

    projects: {
      title: 'المشاريع',
      subtitle: 'مختارات من الأعمال الأخيرة.',
      items: [
        {
          title: 'لوحة تحكم A2Z',
          description:
            'لوحة تحكم تساعد فرق التجارة الإلكترونية على إدارة العمليات اليومية ومتابعة أداء المنصة.',
          tech: ['Flutter', 'Cubit', 'REST API'],
        },
        {
          title: 'نظام الدراسات العليا لمعهد الأورام',
          description:
            'نظام لإدارة برامج الدراسات العليا وتنظيم بيانات الطلاب والعمليات الأكاديمية.',
          tech: ['Flutter', 'Provider', 'API'],
        },
        {
          title: 'منصة كويزاتي',
          description:
            'منصة تعليمية حديثة تساعد المؤسسات على إدارة الدورات والطلاب والاختبارات الإلكترونية مع إنشاء بنوك أسئلة تلقائياً وتشغيل امتحانات آمنة.',
          tech: ['Next.js', 'Node.js', 'PostgreSQL', 'OpenAI API', 'LangChain', 'RAG', 'WebRTC', 'Computer Vision', 'FastAPI', 'WebSockets'],
          link: 'https://www.quizatty.com/Login',
        },
        {
          title: 'نظام شق التعبان',
          description:
            'منصة رقمية وتطبيق موبايل لدعم منطقة شق التعبان الصناعية وتحسين إدارة الخدمات والعمليات.',
          tech: ['تطبيق موبايل', 'تشغيل', 'API'],
          link: 'https://www.shkelteaban.com/',
        },
        {
          title: 'أتمتة بيانات بنك EgyBank',
          description:
            'نظام لأتمتة معالجة البيانات المصرفية وعرضها بصرياً لدعم التحليل واتخاذ القرار.',
          tech: ['أتمتة', 'تصور بيانات', 'قطاع مصرفي'],
        },
        {
          title: 'تطبيق الطقس',
          description:
            'تطبيق موبايل يعرض معلومات الطقس في الوقت الحقيقي بواجهة سهلة الاستخدام.',
          tech: ['تطبيق موبايل', 'Weather API', 'واجهة مستخدم'],
        },
      ],
    },

    contact: {
      eyebrow: 'تواصل',
      title: 'ابدأ مشروعك',
      subtitle:
        'أخبرنا عن فكرتك أو مشروعك وسيتواصل معك فريقنا سريعاً لمناقشة الخطوات القادمة.',
      cards: {
        phoneLabel: 'الهاتف',
        phoneValue: '+20 120 424 6538',
        emailLabel: 'البريد الإلكتروني',
        emailValue: 'nzmfix4@gmail.com',
        locationLabel: 'الموقع',
        locationValue: 'القاهرة، مصر',
      },
      form: {
        nameLabel: 'الاسم',
        namePlaceholder: 'اكتب اسمك الكامل',
        emailLabel: 'البريد الإلكتروني',
        emailPlaceholder: 'you@company.com',
        projectTypeLabel: 'نوع المشروع',
        projectTypePlaceholder: 'اختر نوع المشروع',
        projectTypeOptions: {
          web: 'منصة ويب',
          mobile: 'تطبيق موبايل',
          desktop: 'تطبيق سطح مكتب',
          dashboard: 'لوحة تحكم / تحليلات',
          automation: 'أتمتة / واجهات API',
          other: 'أخرى',
        },
        messageLabel: 'الرسالة',
        messagePlaceholder: 'حدثنا عن أهداف المشروع والجدول الزمني.',
        submit: 'ابدأ المشروع 🚀',
        errors: {
          name: 'يرجى إدخال الاسم',
          email: 'يرجى إدخال البريد الإلكتروني',
          projectType: 'يرجى اختيار نوع المشروع',
          message: 'يرجى إدخال الرسالة',
        },
      },
    },

    footer: {
      text: 'محفظة رقمية من تصميم',
      author: 'يوجي ساتو',
      builtWith: 'بإتقان واهتمام بالتفاصيل',
    },
  },
};
